/**
        * Mailchimp Back In Stock – Upsell Isolation Fix
        * ------------------------------------------------
        * Problem: The Mailchimp BIS widget listens for `variant:change` events at the
        * document level. The Palo Alto theme fires the same event for upsell product
        * blocks, so when an upsell variant is OOS, Mailchimp incorrectly shows the
        * "Get a restock alert" button for the main product.
        *
        * Fix: After every variant:change event (and on initial load), we check whether
        * the *main* product is actually available. If it is, we force-hide the BIS
        * widget. If it genuinely is OOS, we leave it alone.
        *
        * HOW TO INSTALL:
        *   Shopify Admin → Online Store → Themes → Edit code
        *   Open theme.liquid and paste the contents of this file just before </body>
        *   (or add it to your theme's custom JS asset and include it there)
        *
        * CONFIGURATION:
        *   Tweak the three constants at the top of the IIFE if your theme uses
        *   different selectors. The defaults match Palo Alto + Mailchimp BIS.
        */

        (function () {
          'use strict';

          // ─── CONFIG ──────────────────────────────────────────────────────────────────

          /**
          * Selector for the main product's <form action="/cart/add">.
          * Scoped to #MainContent so upsell forms (which live outside it, or later
          * in the DOM) are excluded.
          */
          var MAIN_FORM_SELECTOR = '#MainContent form[action="/cart/add"]';

          /**
          * Selectors to try when locating the Mailchimp BIS widget container.
          * The patch checks these in order and uses the first match.
          * Add your own if the auto-detect misses it.
          */
          var BIS_CONTAINER_SELECTORS = [
            '[class*="mailchimp-bis"]',
            '[id*="mailchimp-bis"]',
            '[class*="mc-bis"]',
            '[id*="mc-bis"]',
            '[class*="back-in-stock"]',
            '[id*="back-in-stock"]',
            '[data-bis-product-id]',
            '[data-mc-bis]',
          ];

          /**
          * Fallback: if none of the above match, we look for a button whose text
          * contains this string (case-insensitive) and hide its nearest wrapper.
          */
          var BIS_BUTTON_TEXT = 'restock';

          // ─── END CONFIG ───────────────────────────────────────────────────────────────

          /** Walk up at most `maxLevels` to find a container that looks like a widget wrapper. */
          function findWrapper(el, maxLevels) {
            var node = el;
            for (var i = 0; i < maxLevels; i++) {
              if (!node.parentElement) break;
              node = node.parentElement;
              // Stop climbing if we've reached a section-level element
              var tag = node.tagName.toLowerCase();
              if (tag === 'section' || tag === 'article' || tag === 'main') break;
              // A tight wrapper (few children) is a good candidate
              if (node.children.length <= 4) return node;
            }
            return el; // fall back to original element
          }

          /** Find the Mailchimp BIS widget element. Returns null if not found. */
          function findBISWidget() {
            // 1. Try explicit class/attribute selectors
            for (var i = 0; i < BIS_CONTAINER_SELECTORS.length; i++) {
              var el = document.querySelector(BIS_CONTAINER_SELECTORS[i]);
              if (el) return el;
            }

            // 2. Fallback: scan buttons/links for matching text
            var candidates = document.querySelectorAll('button, a, input[type="submit"]');
            for (var j = 0; j < candidates.length; j++) {
              var btn = candidates[j];
              if (new RegExp(BIS_BUTTON_TEXT, 'i').test(btn.textContent || btn.value || '')) {
                // Make sure this button lives inside the main product area, not an upsell
                if (btn.closest('#MainContent')) {
                  return findWrapper(btn, 5);
                }
              }
            }

            return null;
          }

          /** Get the main product's <form>. */
          function getMainForm() {
            return document.querySelector(MAIN_FORM_SELECTOR);
          }

          /**
          * Determine if the currently selected variant of the MAIN product is available.
          * Uses three strategies in order of reliability:
          *   1. Shopify product JSON embedded in the page (most accurate)
          *   2. The add-to-cart button's disabled/class state
          *   3. Defaults to "available" if we can't tell
          */
          function isMainProductAvailable() {
            var form = getMainForm();
            if (!form) return true; // Can't find the form — don't suppress BIS

            // Strategy 1: parse variant data from the embedded product JSON script tag.
            // Shopify/Palo Alto typically embeds this as:
            //   <script id="product-json" type="application/json">{ "variants": [...] }</script>
            // or a script tag with a data attribute. We search broadly.
            var variantInput = form.querySelector('input[name="id"]');
            if (variantInput) {
              var selectedId = parseInt(variantInput.value, 10);
              var jsonScripts = document.querySelectorAll(
                'script[type="application/json"], script#product-json, script[data-product-json]'
              );
              for (var i = 0; i < jsonScripts.length; i++) {
                try {
                  var data = JSON.parse(jsonScripts[i].textContent);
                  // The JSON might be the full product object or just variants
                  var variants = data.variants || (Array.isArray(data) ? data : null);
                  if (variants) {
                    for (var v = 0; v < variants.length; v++) {
                      if (variants[v].id === selectedId) {
                        return !!variants[v].available;
                      }
                    }
                  }
                } catch (e) {
                  // JSON parse failed — try next script tag
                }
              }
            }

            // Strategy 2: check the submit button's state.
            // In Palo Alto, an OOS variant disables the button and/or adds a sold-out class.
            var submitBtn = form.querySelector(
              'button[type="submit"]:not([name="goto_pp"]):not([name="cancel"]), input[type="submit"]'
            );
            if (submitBtn) {
              if (submitBtn.disabled) return false;
              if (/sold.?out|unavailable|out.?of.?stock/i.test(submitBtn.className)) return false;
              // Button exists and isn't disabled → treat as available
              return true;
            }

            // Strategy 3: default to available (safer — better to hide BIS wrongly
            // than to show it when the product is in stock)
            return true;
          }

          /**
          * Core correction logic. Called on load and after every variant:change event.
          */
          function correctBISState() {
            var widget = findBISWidget();
            if (!widget) return; // BIS widget not rendered yet — nothing to do

            if (isMainProductAvailable()) {
              // Main product is IN STOCK → Mailchimp should not be showing at all
              widget.style.setProperty('display', 'none', 'important');
            } else {
              // Main product is genuinely OOS → let Mailchimp do its thing
              widget.style.removeProperty('display');
            }
          }

          // ─── INITIALISATION ──────────────────────────────────────────────────────────

          // Run once the DOM is fully parsed
          if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', correctBISState);
          } else {
            correctBISState();
          }

          // React to variant selections anywhere on the page.
          // We wait 150 ms so Mailchimp's own handler fires first, then we correct it.
          document.addEventListener('variant:change', function () {
            setTimeout(correctBISState, 150);
          });

          // Watch for Mailchimp dynamically injecting or toggling its widget
          // (e.g. via style or hidden attribute changes)
          var observer = new MutationObserver(function (mutations) {
            // Only re-run if a relevant change happened near the main product area
            var mainContent = document.getElementById('MainContent');
            for (var i = 0; i < mutations.length; i++) {
              var target = mutations[i].target;
              if (mainContent && mainContent.contains(target)) {
                correctBISState();
                return; // one correction per batch is enough
              }
            }
          });

          observer.observe(document.body, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: ['style', 'class', 'hidden'],
          });

        })();